import { Fragment, useState } from "react";
import { Menu, Transition } from "@headlessui/react";
import { EllipsisVerticalIcon } from "@heroicons/react/20/solid";
import {
  ArchiveBoxIcon,
  ArrowPathIcon,
  ViewfinderCircleIcon,
} from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import ArchiveLibraryRequestModal from "../LibraryRequests/ArchiveLibraryRequestModal";
import {
  useArchiveVqaRequest,
  useUnarchiveVqaRequest,
} from "../../custom-hooks/queries";
import Notification from "../Notification";
import {
  buildSponsorScopedDetailsUrl,
  requestTypeToStatusParam,
} from "../../utils/sponsorScopedDetailsUrl";
import { pendingJoinRequestIdForGridRow } from "../../utils/joinRequestDecision";
import { useJoinRequestDecision } from "../../custom-hooks/useJoinRequestDecision";
import JoinRequestDecisionMenuItems from "./JoinRequestDecisionMenuItems";

/**
 * Kebab menu for Assessment Request grids (matches Innovation Lab UserGridActionMenu styling).
 */
export default function AssessmentRequestGridActionMenu({
  item,
  actionVariant,
  refetch,
}) {
  const navigate = useNavigate();
  // Admin list and API may use snake_case, Pascal, or mixed shapes for JSON
  const id =
    item?.vqa_request_id ??
    item?.Vqa_Request_Id ??
    item?.vqaRequestId;
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [showNote, setShowNote] = useState(false);
  const [noteMessage, setNoteMessage] = useState("");
  const [noteStatus, setNoteStatus] = useState("success");
  const { mutate: archiveMutate, isPending: archivePending } =
    useArchiveVqaRequest();
  const { mutate: unarchiveMutate, isPending: unarchivePending } =
    useUnarchiveVqaRequest();
  const joinRequestId =
    actionVariant === "admin" ? pendingJoinRequestIdForGridRow(item) : null;
  const joinDecision = useJoinRequestDecision({
    notify: (message, noteStatusValue) => {
      setNoteMessage(message);
      setNoteStatus(noteStatusValue);
      setShowNote(true);
    },
    onDecided: () => refetch?.(),
  });

  const statusRaw =
    item?.vqa_request_status ?? item?.vqa_status ?? item?.Vqa_Request_Status ?? "";
  const status = String(statusRaw).trim();
  const isArchived =
    status === "Archived" || String(statusRaw).toLowerCase() === "archived";

  const handleViewDetails = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (id == null) return;
    if (actionVariant === "admin") {
      navigate(
        buildSponsorScopedDetailsUrl(
          `/Backend/Assessment/${id}/Manage`,
          item?.sponsor_name,
          requestTypeToStatusParam(item?.request_type)
        )
      );
    } else {
      navigate(`/Assessment/${id}/Details`);
    }
  };

  return (
    <Menu as="div" className="relative inline-block text-left">
      <div>
        <Menu.Button
          className="inline-flex items-center justify-center rounded-md p-1.5 text-gray-400 hover:text-gray-500 focus:outline-none"
          onClick={(e) => e.stopPropagation()}
        >
          <span className="sr-only">Open options</span>
          <EllipsisVerticalIcon className="h-5 w-5" aria-hidden="true" />
        </Menu.Button>
      </div>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items
          anchor="bottom end"
          portal
          className="z-[100] mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none shadow-ring"
        >
          <div className="py-1">
            <Menu.Item>
              {({ active }) => (
                <button
                  type="button"
                  className={`${
                    active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                  } group flex w-full items-center px-4 py-2 text-sm`}
                  onClick={handleViewDetails}
                >
                  <ViewfinderCircleIcon
                    className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                    aria-hidden="true"
                  />
                  View Details
                </button>
              )}
            </Menu.Item>
            {joinRequestId ? (
              <JoinRequestDecisionMenuItems
                disabled={joinDecision.isDeciding}
                onAccept={() => joinDecision.accept(joinRequestId)}
                onReject={() => joinDecision.reject(joinRequestId)}
              />
            ) : null}
            {actionVariant === "admin" && id != null && isArchived ? (
              <Menu.Item>
                {({ active }) => (
                  <button
                    type="button"
                    disabled={unarchivePending}
                    className={`${
                      active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                    } group flex w-full items-center px-4 py-2 text-sm disabled:opacity-50`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      unarchiveMutate(id, {
                        onSuccess: () => {
                          setNoteMessage("Request unarchived successfully.");
                          setNoteStatus("success");
                          setShowNote(true);
                          refetch?.();
                        },
                        onError: (err) => {
                          setNoteMessage(
                            err?.response?.data?.message ||
                              "Could not unarchive this request."
                          );
                          setNoteStatus("error");
                          setShowNote(true);
                        },
                      });
                    }}
                  >
                    <ArrowPathIcon
                      className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                      aria-hidden="true"
                    />
                    Unarchive Request
                  </button>
                )}
              </Menu.Item>
            ) : null}
            {actionVariant === "admin" && id != null && status !== "Archived" ? (
              <Menu.Item>
                {({ active }) => (
                  <button
                    type="button"
                    className={`${
                      active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                    } group flex w-full items-center px-4 py-2 text-sm`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setArchiveOpen(true);
                    }}
                  >
                    <ArchiveBoxIcon
                      className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                      aria-hidden="true"
                    />
                    Archive Request
                  </button>
                )}
              </Menu.Item>
            ) : null}
          </div>
        </Menu.Items>
      </Transition>
      <ArchiveLibraryRequestModal
        open={archiveOpen}
        onClose={() => setArchiveOpen(false)}
        isSubmitting={archivePending}
        requestLabel={item?.provider_name || ""}
        onConfirm={(archiveComment) => {
          archiveMutate(
            { vqaRequestId: id, archiveComment },
            {
              onSuccess: () => {
                setArchiveOpen(false);
                setNoteMessage("Request archived successfully.");
                setNoteStatus("success");
                setShowNote(true);
                refetch?.();
              },
              onError: (err) => {
                setArchiveOpen(false);
                setNoteMessage(
                  err?.response?.data?.message ||
                    "Could not archive this request."
                );
                setNoteStatus("error");
                setShowNote(true);
              },
            }
          );
        }}
      />
      <Notification
        setShow={setShowNote}
        show={showNote}
        status={noteStatus}
        errorMessage={noteMessage}
      />
    </Menu>
  );
}
